import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, Clock, DollarSign, Edit3, Plus } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, subDays, startOfDay, endOfDay, addDays, parseISO, setDate, addMonths, isValid } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { getPayrollEntries } from '../lib/payroll';
import { getPayrollSettings, type PayrollSettings } from '../lib/payrollSettings';
import { createPayrollRequest, getPayrollRequestsForResource } from '../lib/payrollRequests';
import PayrollChangeRequestModal from './PayrollChangeRequestModal';
import AddMissingEntryModal from './AddMissingEntryModal';
import ChangeRequestBadge from './ChangeRequestBadge';
import RequestFlatRateEntriesModal from './RequestFlatRateEntriesModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  resource_avatar_url?: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
}

interface UserInfo {
  resourceId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string;
}

interface EarningsReportProps {
  userInfo: UserInfo;
}

// Helper functions
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

// getCurrentPeriodRange function
const getCurrentPeriodRange = (
  frequency: string, 
  startDate: string,
  customPeriod1Start?: string,
  customPeriod1End?: string,
  customPeriod2Start?: string,
  customPeriod2End?: string
) => {
  const today = new Date();
  const currentDay = today.getDate();
  
  switch (frequency) {
    case 'weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentWeekOffset = Math.floor(daysSinceStart / 7) * 7;
      const currentPeriodStart = addDays(periodStart, currentWeekOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 6);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'bi-weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentPeriodOffset = Math.floor(daysSinceStart / 14) * 14;
      const currentPeriodStart = addDays(periodStart, currentPeriodOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 13);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'semi-monthly': {
      if (currentDay <= 15) {
        return {
          start: setDate(startOfMonth(today), 1),
          end: setDate(startOfMonth(today), 15)
        };
      } else {
        return {
          start: setDate(startOfMonth(today), 16),
          end: endOfMonth(today)
        };
      }
    }
    
    case 'monthly': {
      const periodStart = parseISO(startDate);
      const startDay = periodStart.getDate();
      let currentPeriodStart: Date;
      
      if (currentDay >= startDay) {
        currentPeriodStart = setDate(today, startDay);
      } else {
        currentPeriodStart = setDate(subMonths(today, 1), startDay);
      }
      
      const currentPeriodEnd = addDays(setDate(addDays(currentPeriodStart, 32), startDay), -1);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'custom': {
      if (!customPeriod1Start || !customPeriod1End || !customPeriod2Start || !customPeriod2End) {
        return { start: startOfMonth(today), end: endOfMonth(today) };
      }
      
      const p1Start = parseInt(customPeriod1Start);
      const p1End = parseInt(customPeriod1End);
      const p2Start = parseInt(customPeriod2Start);
      const p2End = parseInt(customPeriod2End);
      
      // Handle period crossing month boundary (e.g., 26th-10th)
      if (p1Start > p1End) {
        if (currentDay >= p1Start || currentDay <= p1End) {
          const periodStart = currentDay >= p1Start 
            ? setDate(today, p1Start)
            : setDate(subMonths(today, 1), p1Start);
          const periodEnd = currentDay >= p1Start
            ? setDate(addMonths(today, 1), p1End)
            : setDate(today, p1End);
          return { start: periodStart, end: periodEnd };
        }
      } else if (currentDay >= p1Start && currentDay <= p1End) {
        return {
          start: setDate(today, p1Start),
          end: setDate(today, p1End)
        };
      }
      
      if (p2Start > p2End) {
        if (currentDay >= p2Start || currentDay <= p2End) {
          const periodStart = currentDay >= p2Start
            ? setDate(today, p2Start)
            : setDate(subMonths(today, 1), p2Start);
          const periodEnd = currentDay >= p2Start
            ? setDate(addMonths(today, 1), p2End)
            : setDate(today, p2End);
          return { start: periodStart, end: periodEnd };
        }
      } else if (currentDay >= p2Start && currentDay <= p2End) {
        return {
          start: setDate(today, p2Start),
          end: setDate(today, p2End)
        };
      }
      
      // Default fallback
      return { start: startOfMonth(today), end: endOfMonth(today) };
    }
    
    default:
      return { start: startOfMonth(today), end: endOfMonth(today) };
  }
};

const DATE_RANGES = [
  { label: 'Current Period', value: 'current_period', getRange: () => {
    return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
  }},
  { label: 'Today', value: 'today', getRange: () => {
    const today = new Date();
    return { start: startOfDay(today), end: endOfDay(today) };
  }},
  { label: 'Yesterday', value: 'yesterday', getRange: () => {
    const yesterday = subDays(new Date(), 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  }},
  { label: 'Select Day', value: 'select_day', getRange: (selectedDay?: Date) => {
    const day = selectedDay || new Date();
    return { start: startOfDay(day), end: endOfDay(day) };
  }},
  { label: 'Last 30 days', value: 'last_30_days', getRange: () => ({
    start: subDays(new Date(), 29),
    end: new Date()
  })},
  { label: 'This Month', value: 'this_month', getRange: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  })},
  { label: 'Last Month', value: 'last_month', getRange: () => {
    const lastMonth = subMonths(new Date(), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  }},
  { label: 'Custom Range', value: 'custom_range', getRange: (customStartDate?: Date, customEndDate?: Date) => ({
    start: customStartDate || startOfMonth(new Date()),
    end: customEndDate || endOfMonth(new Date())
  })}
];

export default function EarningsReport({ userInfo }: EarningsReportProps) {
  const { selectedBusiness } = useBusiness();
  const [entries, setEntries] = useState<PayrollEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(DATE_RANGES.find(r => r.value === 'current_period')!);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCustomRangePickerOpen, setIsCustomRangePickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState(selectedRange.getRange());
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Default to collapsed for earnings report
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dateRangeButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const customRangeRef = useRef<HTMLDivElement>(null);

  // Get resource info from first entry or from userInfo
  const [resourceInfo, setResourceInfo] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    compensation_type: string;
    flat_rate_amount: number | null;
    hourly_rate: number | null;
    avatar_url?: string;
  } | null>(null);

  // Change request modal state
  const [isChangeRequestModalOpen, setIsChangeRequestModalOpen] = useState(false);
  const [requestingEntry, setRequestingEntry] = useState<PayrollEntry | null>(null);
  
  // Add missing entry modal state
  const [isAddMissingEntryModalOpen, setIsAddMissingEntryModalOpen] = useState(false);
  
  // Flat rate request modal state
  const [isFlatRateRequestModalOpen, setIsFlatRateRequestModalOpen] = useState(false);
  const [requestingBooking, setRequestingBooking] = useState<{ id: string; number: number; title: string } | null>(null);
  
  // Track pending change requests for entries
  const [pendingRequests, setPendingRequests] = useState<Record<string, any>>({});

  // Load payroll settings when component mounts
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadPayrollSettings();
    }
  }, [selectedBusiness?.id]);

  // Get resource info when userInfo changes
  useEffect(() => {
    if (userInfo.resourceId) {
      loadResourceInfo();
    }
  }, [userInfo.resourceId]);

  // Update date range when selection or payroll settings change
  useEffect(() => {
    if (selectedRange.value === 'current_period' && payrollSettings) {
      const currentPeriod = getCurrentPeriodRange(
        payrollSettings.frequency,
        payrollSettings.start_date,
        payrollSettings.custom_period_1_start,
        payrollSettings.custom_period_1_end,
        payrollSettings.custom_period_2_start,
        payrollSettings.custom_period_2_end
      );
      setDateRange(currentPeriod);
    } else if (selectedRange.value === 'select_day') {
      setDateRange(selectedRange.getRange(selectedDay || new Date()));
    } else if (selectedRange.value === 'custom_range') {
      setDateRange(selectedRange.getRange(customStartDate || undefined, customEndDate || undefined));
    } else {
      setDateRange(selectedRange.getRange());
    }
  }, [selectedRange, selectedDay, customStartDate, customEndDate, payrollSettings]);

  // Load entries when date range changes
  useEffect(() => {
    loadEntries();
  }, [dateRange, selectedBusiness?.id, userInfo.resourceId]);

  const loadResourceInfo = async () => {
    if (!userInfo.resourceId || !selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('resources')
        .select('first_name, last_name, email, compensation_type, flat_rate_amount, hourly_rate, avatar_url')
        .eq('id', userInfo.resourceId)
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) throw error;
      if (data) {
        setResourceInfo(data);
      }
    } catch (err) {
      console.error('Error loading resource info:', err);
    }
  };

  const loadPayrollSettings = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const settings = await getPayrollSettings(selectedBusiness.id);
      if (settings) {
        setPayrollSettings(settings);
      }
    } catch (error) {
      console.error('Error loading payroll settings:', error);
    }
  };

  const loadEntries = async () => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      setLoading(true);
      const data = await getPayrollEntries(
        selectedBusiness.id,
        format(dateRange.start, 'yyyy-MM-dd'),
        format(dateRange.end, 'yyyy-MM-dd')
      );
      
      // Filter entries to only show those for the logged-in resource
      const filteredEntries = data.filter(entry => entry.resource_id === userInfo.resourceId);
      setEntries(filteredEntries as PayrollEntry[]);
      
      // Load pending change requests for these entries
      await loadPendingRequests();
    } catch (err) {
      console.error('Error loading earnings entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      const requests = await getPayrollRequestsForResource(userInfo.resourceId, selectedBusiness.id);
      
      // Create a mapping of payroll_entry_id -> request for pending requests
      // Only include change requests (not add requests) since add requests don't have payroll_entry_id
      const requestMap = requests
        .filter(request => request.status === 'pending' && request.request_type === 'change' && request.payroll_entry_id)
        .reduce((acc, request) => {
          if (request.payroll_entry_id) {
            acc[request.payroll_entry_id] = request;
          }
          return acc;
        }, {} as Record<string, any>);
      
      setPendingRequests(requestMap);
      console.log('Loaded pending requests:', requestMap);
    } catch (err) {
      console.error('Error loading pending requests:', err);
    }
  };

  const toggleBooking = (bookingId: string) => {
    setExpandedBookings(prev => {
      const next = new Set(prev);
      if (next.has(bookingId)) {
        next.delete(bookingId);
      } else {
        next.add(bookingId);
      }
      return next;
    });
  };

  // Change request handlers
  const handleRequestChange = (entry: PayrollEntry) => {
    setRequestingEntry({
      ...entry,
      hourly_rate: entry.hourly_rate || resourceInfo?.hourly_rate || 0
    });
    setIsChangeRequestModalOpen(true);
  };

  const handleSubmitChangeRequest = async (requestData: any) => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      await createPayrollRequest({
        ...requestData,
        business_id: selectedBusiness.id,
        resource_id: userInfo.resourceId,
        request_type: 'change'
      });
      
      toast.success('Change request submitted successfully! It will be reviewed by management.');
      setIsChangeRequestModalOpen(false);
      setRequestingEntry(null);
      
      // Reload pending requests to show the new badge
      await loadPendingRequests();
    } catch (error) {
      console.error('Error submitting change request:', error);
      throw error; // Re-throw to let modal handle the error display
    }
  };

  const handleCloseChangeRequestModal = () => {
    setIsChangeRequestModalOpen(false);
    setRequestingEntry(null);
  };

  const handleAddMissingEntry = () => {
    setIsAddMissingEntryModalOpen(true);
  };

  const handleAddMissingEntrySuccess = async () => {
    await loadEntries(); // Reload entries
    await loadPendingRequests(); // Reload pending requests
  };

  const handleCloseAddMissingEntryModal = () => {
    setIsAddMissingEntryModalOpen(false);
  };

  const handleRequestFlatRateEntries = (bookingId: string, bookingNumber: number, bookingTitle: string) => {
    setRequestingBooking({ id: bookingId, number: bookingNumber, title: bookingTitle });
    setIsFlatRateRequestModalOpen(true);
  };

  const handleCloseFlatRateRequestModal = () => {
    setIsFlatRateRequestModalOpen(false);
    setRequestingBooking(null);
  };

  const handleFlatRateRequestSuccess = () => {
    loadPendingRequests(); // Reload pending requests to show new flat rate requests
    loadEntries(); // Reload data to refresh the view
  };

  // Calculate dropdown position when opening
  const calculateDropdownPosition = () => {
    if (dateRangeButtonRef.current) {
      const rect = dateRangeButtonRef.current.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      
      setDropdownPosition({
        top: rect.bottom + scrollY + 8, // 8px offset
        left: rect.right + scrollX - 288 // 288px is dropdown width (w-72 = 18rem = 288px), align to right edge
      });
    }
  };

  // Handle dropdown toggle
  const handleDateRangeToggle = () => {
    if (!isDateRangeOpen) {
      calculateDropdownPosition();
    }
    setIsDateRangeOpen(!isDateRangeOpen);
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    if (!isDateRangeOpen && !isDatePickerOpen && !isCustomRangePickerOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is inside any of our dropdown elements
      const isInsideButton = dateRangeButtonRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isInsideDatePicker = datePickerRef.current?.contains(target);
      const isInsideCustomRange = customRangeRef.current?.contains(target);
      
      if (!isInsideButton && !isInsideDropdown && !isInsideDatePicker && !isInsideCustomRange) {
        setIsDateRangeOpen(false);
        setIsDatePickerOpen(false);
        setIsCustomRangePickerOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDateRangeOpen(false);
        setIsDatePickerOpen(false);
        setIsCustomRangePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDateRangeOpen, isDatePickerOpen, isCustomRangePickerOpen]);

  // Group entries by type and booking (exactly like PayrollResourceSummary)
  const entriesData = entries.reduce<{
    timeclock: {
      entries: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    }>;
    totalHours: number;
    totalEarnings: number;
  }>((acc, entry) => {
    if (entry.entry_type === 'timeclock') {
      // Add to timeclock entries
      acc.timeclock.entries.push(entry);
      acc.timeclock.totalHours += entry.hours || 0;
      acc.timeclock.totalEarnings += entry.earnings || 0;
    } else {
      // Add to booking entries (flat_rate and commission)
      if (entry.booking_id && !acc.bookings[entry.booking_id]) {
        acc.bookings[entry.booking_id] = {
          booking_number: entry.booking_number || 0,
          booking_title: entry.booking_title || '',
          start_date: entry.start_date || entry.created_at,
          end_date: entry.end_date || entry.created_at,
          items: [],
          totalHours: 0,
          totalEarnings: 0
        };
      }

      if (entry.booking_id) {
        acc.bookings[entry.booking_id].items.push(entry);
        if (entry.entry_type === 'flat_rate') {
          acc.bookings[entry.booking_id].totalHours += entry.hours || 0;
        }
        acc.bookings[entry.booking_id].totalEarnings += entry.earnings || 0;
      }
    }

    // Update totals
    if (entry.entry_type === 'flat_rate') {
      acc.totalHours += entry.hours || 0;
    } else if (entry.entry_type === 'timeclock') {
      acc.totalHours += entry.hours || 0;
    }
    acc.totalEarnings += entry.earnings || 0;

    return acc;
  }, {
    timeclock: { entries: [], totalHours: 0, totalEarnings: 0 },
    bookings: {},
    totalHours: 0,
    totalEarnings: 0
  });

  if (loading || !resourceInfo) {
    return (
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-4 sm:p-6 md:p-8" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        <div className="flex items-center justify-center h-48 sm:h-64">
          <div className="text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-purple-600/30"></div>
            <p className="mt-4 text-sm sm:text-base text-gray-600 font-medium">Loading earnings report...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl overflow-hidden">
      {/* Header with Date Range Selector */}
      <div className="px-3 sm:px-4 md:px-8 py-3 sm:py-4 md:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Earnings Summary</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
              {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
            </p>
          </div>
          
          {/* Add Missing Entry Button - Mobile Optimized - Only for hourly resources */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {resourceInfo?.compensation_type === 'hourly' && (
              <button
                onClick={handleAddMissingEntry}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 border border-blue-500/20 rounded-xl px-3 sm:px-4 py-2.5 sm:py-2.5 shadow-lg transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 touch-manipulation w-full sm:w-auto"
                style={{ minHeight: '44px' }}
                title="Add a missing time entry"
              >
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <div className="p-1 bg-white/20 rounded-lg flex-shrink-0">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  {/* Show full text on larger screens, abbreviated on mobile */}
                  <span className="text-sm font-medium text-white">
                    <span className="hidden sm:inline">Add Missing Entry</span>
                    <span className="sm:hidden">Add Entry</span>
                  </span>
                </div>
              </button>
            )}
            
            {/* Date Range Selector */}
          <div className="relative flex-shrink-0">
            <button
              ref={dateRangeButtonRef}
              onClick={handleDateRangeToggle}
              className="group relative overflow-hidden bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-lg transition-all duration-200 hover:bg-white/80 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500/30 touch-manipulation w-full sm:w-auto"
              style={{ minHeight: '44px' }}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1 sm:p-1.5 bg-purple-500/10 rounded-lg flex-shrink-0">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{selectedRange.label}</div>
                  {selectedRange.value === 'custom_range' && customStartDate && customEndDate && (
                    <div className="text-xs text-gray-500 truncate">
                      {format(customStartDate, 'MMM d')} - {format(customEndDate, 'MMM d')}
                    </div>
                  )}
                </div>
                <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isDateRangeOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            </div>
          </div>
          
          {/* Portal-based Dropdowns */}
          {isDateRangeOpen && createPortal(
            <div 
              ref={dropdownRef}
              className="fixed w-72 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl py-2 z-50 animate-in slide-in-from-top-2"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}
            >
              {DATE_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => {
                    setSelectedRange(range);
                    if (range.value === 'select_day') {
                      setIsDatePickerOpen(true);
                    } else if (range.value === 'custom_range') {
                      setIsCustomRangePickerOpen(true);
                    } else {
                      setIsDateRangeOpen(false);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                    selectedRange.value === range.value
                      ? 'bg-purple-50 text-purple-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>,
            document.body
          )}

          {/* Date Picker Portal */}
          {isDatePickerOpen && createPortal(
            <div 
              ref={datePickerRef}
              className="fixed p-4 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl z-50"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}
            >
              <input
                type="date"
                value={selectedDay ? format(selectedDay, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                  setSelectedDay(date);
                  setIsDatePickerOpen(false);
                  setIsDateRangeOpen(false);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
              />
            </div>,
            document.body
          )}
          
          {/* Custom Range Picker Portal */}
          {isCustomRangePickerOpen && createPortal(
            <div 
              ref={customRangeRef}
              className="fixed p-6 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl z-50 w-80"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left - 32, // Adjust for wider width
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                      setCustomStartDate(date);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                      setCustomEndDate(date);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => {
                      setIsCustomRangePickerOpen(false);
                      setIsDateRangeOpen(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors duration-150"
                  >
                    Apply Range
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Resource Header - Exact copy of PayrollResourceSummary structure */}
      <div className="transition-all duration-300">
        <div 
          className="group p-4 sm:p-6 lg:p-8 cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50/30 hover:to-white/50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            {/* Avatar & Basic Info Row */}
            <div className="flex items-center gap-4 flex-1">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-sm sm:text-base lg:text-lg font-bold text-white overflow-hidden shadow-lg">
                  {resourceInfo.avatar_url ? (
                    <img 
                      src={resourceInfo.avatar_url} 
                      alt={`${resourceInfo.first_name} ${resourceInfo.last_name}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    `${resourceInfo.first_name[0]}${resourceInfo.last_name[0]}`
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                </div>
              </div>

              {/* Resource Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                    {resourceInfo.first_name} {resourceInfo.last_name}
                  </h3>
                  <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium w-fit">
                    {resourceInfo.compensation_type.replace('_', ' ').toUpperCase()}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{resourceInfo.email}</p>
                
                {/* Total Earnings - Mobile */}
                <div className="sm:hidden mt-3 flex items-center justify-between">
                  <div>
                    <div className="text-xl font-bold text-gray-900">
                      ${entriesData.totalEarnings.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">Total Earnings</div>
                  </div>
                  <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 group-hover:bg-gray-50 transition-colors duration-200">
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid - Hidden on Mobile, Shown on Tablet+ */}
            <div className="hidden sm:block">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 w-full">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <Clock className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Time</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    {formatDuration(entriesData.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Clock</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${entriesData.timeclock.totalEarnings.toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Flat</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${Object.values(entriesData.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 lg:p-3 border border-white/30">
                  <div className="flex items-center gap-1 lg:gap-2 mb-1">
                    <DollarSign className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Comm.</span>
                  </div>
                  <div className="text-xs lg:text-sm font-bold text-gray-900">
                    ${Object.values(entriesData.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Total Earnings & Expand Button - Desktop */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-right">
                <div className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">
                  ${entriesData.totalEarnings.toFixed(2)}
                </div>
                <div className="text-xs lg:text-sm text-gray-500">
                  Total Earnings
                </div>
              </div>
              <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 group-hover:bg-gray-50 transition-colors duration-200">
                <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`} />
              </div>
            </div>

            {/* Mobile Stats Grid - Shown when expanded on mobile */}
            {isExpanded && (
              <div className="sm:hidden mt-4 grid grid-cols-2 gap-3">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Total Time</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {formatDuration(entriesData.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Timeclock</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${entriesData.timeclock.totalEarnings.toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Flat Rate</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${Object.values(entriesData.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Commission</span>
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    ${Object.values(entriesData.bookings).reduce((sum, booking) => 
                      sum + booking.items.reduce((itemSum, item) => 
                        itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                        0
                      ), 0).toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content - Exact copy of PayrollResourceSummary structure */}
        {isExpanded && (
          <div className="bg-gradient-to-r from-gray-50/50 to-white/30 border-t border-gray-100/50">
            {/* Timeclock Entries */}
            {entriesData.timeclock.entries.length > 0 && (
              <div className="p-4 sm:p-6 border-b border-gray-100/50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-500/10 rounded-xl">
                      <Clock className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Time Clock Entries</h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        ${entriesData.timeclock.totalEarnings.toFixed(2)} total earnings
                      </p>
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-gray-500">
                    {formatDuration(entriesData.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                  </div>
                </div>
               
               <div className="grid gap-4">
                 {entriesData.timeclock.entries.map((entry) => (
                   <div key={entry.id} className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm hover:shadow-md transition-all duration-200">
                     <div className="flex items-center justify-between">
                       <div className="flex-1">
                         <div className="flex items-center gap-3 mb-2">
                           <Calendar className="h-4 w-4 text-gray-400" />
                           <span className="text-sm font-medium text-gray-900">
                             {format(new Date(entry.clock_in || entry.created_at), 'MMM d, yyyy')}
                           </span>
                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                             entry.process_status === 'PAID' 
                               ? 'bg-green-100 text-green-700' 
                               : 'bg-yellow-100 text-yellow-700'
                           }`}>
                             {entry.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                           </span>
                         </div>
                         
                         <div className="flex items-center justify-between text-sm text-gray-600">
                           <span>
                             {entry.clock_in ? format(new Date(entry.clock_in), 'h:mm a') : '-'} - {
                               entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : 'In Progress'
                             }
                           </span>
                         </div>
                         
                         {entry.break_start && (
                           <div className="mt-2 text-xs text-gray-500">
                             Break: {format(new Date(entry.break_start), 'h:mm a')} - {
                               entry.break_end ? format(new Date(entry.break_end), 'h:mm a') : 'In Progress'
                             }
                           </div>
                         )}
                       </div>
                       
                       <div className="flex items-center gap-3">
                         <div className="text-right">
                           <div className="text-lg font-bold text-gray-900 mb-1">
                             {formatDuration(entry.total_minutes || 0)}
                           </div>
                           <div className="text-sm font-semibold text-gray-700">
                             ${entry.earnings?.toFixed(2) || '0.00'}
                           </div>
                         </div>
                         
                         {/* Request Change Button - Only for unpaid timeclock entries without pending requests */}
                         {entry.entry_type === 'timeclock' && entry.process_status !== 'PAID' && !pendingRequests[entry.id] && (
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               handleRequestChange(entry);
                             }}
                             className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150 flex items-center gap-1"
                             title="Request change to this time entry"
                           >
                             <Edit3 className="h-4 w-4" />
                             <span className="text-xs font-medium hidden sm:inline">Request Change</span>
                           </button>
                         )}
                       </div>
                     </div>
                     
                     {/* Change Request Badge */}
                     {pendingRequests[entry.id] && (
                       <ChangeRequestBadge 
                         request={pendingRequests[entry.id]}
                         originalEntry={{
                           clock_in: entry.clock_in,
                           clock_out: entry.clock_out,
                           total_minutes: entry.total_minutes
                         }}
                       />
                     )}
                   </div>
                 ))}
               </div>
             </div>
           )}

           {/* Booking Entries */}
           {Object.entries(entriesData.bookings).map(([bookingId, booking]) => (
             <div key={bookingId} className="border-b border-gray-100/50 last:border-b-0">
               <div 
                 className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-white/30 transition-colors duration-200"
                 onClick={() => toggleBooking(bookingId)}
               >
                 <div className="flex items-center gap-3 flex-1 min-w-0">
                   <div className="p-2 bg-gray-500/10 rounded-xl flex-shrink-0">
                     <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                   </div>
                   <div className="flex-1 min-w-0">
                     <div className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                       Job #{booking.booking_number} - {booking.booking_title}
                     </div>
                     {booking.end_date && (
                       <div className="text-xs sm:text-sm text-gray-500 mt-1">
                         Completed {format(parseISO(booking.end_date), 'MMM d, yyyy')}
                       </div>
                     )}
                     <div className="text-xs sm:text-sm text-gray-600 mt-1">
                       {booking.totalHours.toFixed(1)}h • ${booking.totalEarnings.toFixed(2)}
                     </div>
                   </div>
                 </div>
                 
                 {/* Request New Entries Button - Desktop */}
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handleRequestFlatRateEntries(bookingId, booking.booking_number, booking.booking_title);
                   }}
                   className="mr-3 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors duration-200 hidden sm:flex items-center gap-1"
                   title="Request additional flat rate entries for this job"
                 >
                   <Plus className="h-3 w-3" />
                   <span className="hidden lg:inline">Request New Entries</span>
                   <span className="lg:hidden">Add</span>
                 </button>
                 
                 {/* Request New Entries Button - Mobile */}
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handleRequestFlatRateEntries(bookingId, booking.booking_number, booking.booking_title);
                   }}
                   className="sm:hidden mr-2 p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 flex items-center justify-center"
                   title="Request additional flat rate entries for this job"
                 >
                   <Plus className="h-4 w-4" />
                 </button>
                 <div className="p-2 rounded-xl bg-white/60 backdrop-blur-sm border border-white/30 flex-shrink-0">
                   <ChevronDown 
                     className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                       expandedBookings.has(bookingId) ? 'rotate-180' : ''
                     }`}
                   />
                 </div>
               </div>
               
               {expandedBookings.has(bookingId) && (
                 <div className="bg-white/40 backdrop-blur-sm">
                   {/* Desktop Table */}
                   <div className="hidden lg:block overflow-hidden">
                     <table className="min-w-full">
                       <thead className="bg-white/60 backdrop-blur-sm">
                         <tr>
                           <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                             Item
                           </th>
                           <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                             Date
                           </th>
                           <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                             Hours
                           </th>
                           <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                             Rate
                           </th>
                           <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                             Total
                           </th>
                         </tr>
                       </thead>
                       <tbody className="divide-y divide-gray-100/50">
                         {booking.items.map((item) => (
                           <tr key={item.id} className="hover:bg-white/60 transition-colors duration-150">
                             <td className="px-6 py-4">
                               <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                               {item.item_description && (
                                 <div className="text-sm text-gray-500 mt-1">{item.item_description}</div>
                               )}
                             </td>
                             <td className="px-6 py-4 text-sm text-gray-600">
                               {formatDate(item.completed_at || item.created_at)}
                             </td>
                             <td className="px-6 py-4 text-sm font-medium text-gray-900">
                               {item.entry_type === 'flat_rate' ? `${item.hours.toFixed(1)}h` : '-'}
                             </td>
                             <td className="px-6 py-4 text-sm text-gray-600">
                               {item.entry_type === 'commission' 
                                 ? 'Commission'
                                 : `$${item.compensation_type === 'flat_rate' 
                                   ? item.flat_rate_amount?.toFixed(2)
                                   : item.hourly_rate?.toFixed(2)
                                 }/hr`
                               }
                             </td>
                             <td className="px-6 py-4">
                               <div className="flex items-center justify-between">
                                 <div className="text-sm font-bold text-gray-900">
                                   ${item.earnings?.toFixed(2) || '0.00'}
                                 </div>
                                 <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                   item.process_status === 'PAID' 
                                     ? 'bg-green-100 text-green-700' 
                                     : 'bg-yellow-100 text-yellow-700'
                                 }`}>
                                   {item.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                                 </span>
                               </div>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>

                   {/* Mobile/Tablet Cards */}
                   <div className="lg:hidden p-4 space-y-3">
                     {booking.items.map((item) => (
                       <div key={item.id} className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                         <div className="flex justify-between items-start mb-3">
                           <div className="flex-1 min-w-0">
                             <h4 className="font-medium text-gray-900 text-sm truncate">{item.item_name}</h4>
                             {item.item_description && (
                               <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                             )}
                           </div>
                           <span className={`px-2 py-1 rounded-full text-xs font-medium ml-3 ${
                             item.process_status === 'PAID' 
                               ? 'bg-green-100 text-green-700' 
                               : 'bg-yellow-100 text-yellow-700'
                           }`}>
                             {item.process_status === 'PAID' ? 'PAID' : 'UNPAID'}
                           </span>
                         </div>
                         
                         <div className="grid grid-cols-2 gap-4 text-xs">
                           <div>
                             <span className="text-gray-500">Date:</span>
                             <div className="font-medium text-gray-900">
                               {formatDate(item.completed_at || item.created_at)}
                             </div>
                           </div>
                           <div>
                             <span className="text-gray-500">Hours:</span>
                             <div className="font-medium text-gray-900">
                               {item.entry_type === 'flat_rate' ? `${item.hours.toFixed(1)}h` : '-'}
                             </div>
                           </div>
                           <div>
                             <span className="text-gray-500">Rate:</span>
                             <div className="font-medium text-gray-900">
                               {item.entry_type === 'commission' 
                                 ? 'Commission'
                                 : `$${item.compensation_type === 'flat_rate' 
                                   ? item.flat_rate_amount?.toFixed(2)
                                   : item.hourly_rate?.toFixed(2)
                                 }/hr`
                               }
                             </div>
                           </div>
                           <div>
                             <span className="text-gray-500">Total:</span>
                             <div className="font-bold text-gray-900">
                               ${item.earnings?.toFixed(2) || '0.00'}
                             </div>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}
             </div>
           ))}
         </div>
       )}
     </div>

         {/* PayrollChangeRequestModal */}
    <PayrollChangeRequestModal
      isOpen={isChangeRequestModalOpen}
      onClose={handleCloseChangeRequestModal}
      entry={requestingEntry}
      onSubmit={handleSubmitChangeRequest}
    />

    {/* AddMissingEntryModal */}
    <AddMissingEntryModal
      isOpen={isAddMissingEntryModalOpen}
      onClose={handleCloseAddMissingEntryModal}
      resourceId={userInfo.resourceId || ''}
      businessId={selectedBusiness?.id || ''}
      onSuccess={handleAddMissingEntrySuccess}
    />

    {/* RequestFlatRateEntriesModal */}
    {requestingBooking && (
      <RequestFlatRateEntriesModal
        isOpen={isFlatRateRequestModalOpen}
        onClose={handleCloseFlatRateRequestModal}
        bookingId={requestingBooking.id}
        bookingNumber={requestingBooking.number}
        bookingTitle={requestingBooking.title}
        businessId={selectedBusiness?.id || ''}
        resourceId={userInfo?.resourceId || ''}
        onSuccess={handleFlatRateRequestSuccess}
      />
    )}
  </div>
 );
} 